import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { componentThumbnailPackageEntries, importComponentThumbnailsFromPackageEntries } from "@/document/previews/componentThumbnails";
import { DOCUMENT_THUMBNAIL_PACKAGE_PATH } from "@/document/previews/documentThumbnail";
import { documentWithExternalizedImageSources, documentWithoutEmbeddedImageSources, imagePaintsInDocument, rasterAssetBytes, registerRasterAssetBytes } from "@/document/assets/rasterAssets";
import { toCurvaDocumentJson, type CurvaMetadataJson } from "@/document/schema/curvaFile";
import { migrateCurvaDocumentJson } from "@/document/schema/migrations";
import type { CurvaDocument } from "@/document/model/document";

export interface CurvaPackage {
  document: CurvaDocument;
  metadata: CurvaMetadataJson;
}

export interface DeserializeCurvaPackageOptions {
  importPreviews?: boolean;
}

export interface SerializeCurvaPackageOptions {
  /** Finder / file-browser preview; omit when unavailable (autosave, headless). */
  documentThumbnailPng?: Uint8Array;
}

export function serializeCurvaPackage(
  document: CurvaDocument,
  options: SerializeCurvaPackageOptions = {},
): Uint8Array {
  const externalizedDocument = documentWithoutEmbeddedImageSources(documentWithExternalizedImageSources(document));
  const metadata: CurvaMetadataJson = {
    format: "curva",
    version: 1,
    savedAt: new Date().toISOString(),
    generator: "curva",
  };

  const entries: Record<string, Uint8Array> = {
    "document.json": strToU8(stableJsonStringify(toCurvaDocumentJson(externalizedDocument))),
    "metadata.json": strToU8(stableJsonStringify(metadata)),
    "assets/": new Uint8Array(),
    "assets/raster/": new Uint8Array(),
    "previews/": new Uint8Array(),
    "previews/components/": new Uint8Array(),
    ...componentThumbnailPackageEntries(externalizedDocument),
  };

  if (options.documentThumbnailPng && options.documentThumbnailPng.byteLength > 0) {
    entries[DOCUMENT_THUMBNAIL_PACKAGE_PATH] = options.documentThumbnailPng;
  }

  for (const paint of imagePaintsInDocument(externalizedDocument)) {
    const assetId = paint.assetId;
    if (!assetId) {
      continue;
    }
    const asset = rasterAssetBytes(assetId, paint.source);
    if (asset) {
      entries[`assets/raster/${assetId}`] = asset.bytes;
    }
  }

  return zipSync(entries, { level: 6 });
}

/** Read the baked document thumbnail from a `.curva` package, if present. */
export function readDocumentThumbnailFromPackage(bytes: Uint8Array): Uint8Array | undefined {
  const entries = unzipSync(bytes);
  const thumbnail = entries[DOCUMENT_THUMBNAIL_PACKAGE_PATH];
  return thumbnail && thumbnail.byteLength > 0 ? thumbnail : undefined;
}

export function deserializeCurvaPackage(bytes: Uint8Array, options: DeserializeCurvaPackageOptions = {}): CurvaPackage {
  const { importPreviews = true } = options;
  const entries = unzipSync(bytes);
  if (importPreviews) {
    importComponentThumbnailsFromPackageEntries(entries);
  }
  const documentEntry = entries["document.json"];
  const metadataEntry = entries["metadata.json"];

  if (!documentEntry) {
    throw new Error(".curva package is missing document.json.");
  }

  const documentJson = migrateCurvaDocumentJson(JSON.parse(strFromU8(documentEntry)));
  const mimeTypeByAssetId = new Map(
    imagePaintsInDocument(documentJson.document).flatMap(paint => paint.assetId ? [[paint.assetId, paint.mimeType] as const] : []),
  );
  for (const [path, bytes] of Object.entries(entries)) {
    if (!path.startsWith("assets/raster/") || path === "assets/raster/") {
      continue;
    }
    const assetId = path.slice("assets/raster/".length);
    registerRasterAssetBytes({
      id: assetId,
      bytes,
      mimeType: mimeTypeByAssetId.get(assetId) ?? mimeTypeForRasterPath(path),
    });
  }

  return {
    document: documentWithExternalizedImageSources(documentJson.document),
    metadata: metadataEntry
      ? JSON.parse(strFromU8(metadataEntry))
      : {
          format: "curva",
          version: 1,
          savedAt: documentJson.document.metadata.updatedAt,
          generator: "curva",
        },
  };
}

function mimeTypeForRasterPath(path: string): string | undefined {
  const lower = path.toLowerCase();
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lower.endsWith(".webp")) {
    return "image/webp";
  }
  if (lower.endsWith(".gif")) {
    return "image/gif";
  }
  if (lower.endsWith(".svg")) {
    return "image/svg+xml";
  }
  return "image/png";
}

export function stableJsonStringify(value: unknown): string {
  return `${JSON.stringify(sortJson(value))}\n`;
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJson);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, sortJson(nested)]),
    );
  }

  return value;
}
